import { useCallback } from 'react'
import { z } from 'zod'
import type { GuideSource } from '../../../shared/routing/guide-target'
import type { GuideSeenResult } from '../../guide'
import { recommendationsApi } from '../infrastructure/recommendations-api'
import { toStoryFailure } from '../infrastructure/browser-adapters'
export function useAddressedRecommendationSeen(
  account: string,
  isCurrent: () => boolean,
) {
  const [markSeen] = recommendationsApi.useRecommendationSeenMutation()
  const [loadCatalog] = recommendationsApi.useLazyRecommendationCatalogQuery()
  return useCallback(
    async (
      source: GuideSource,
      sectionId: string,
      revisionId: string,
    ): Promise<GuideSeenResult> => {
      if (
        !isCurrent() ||
        source.account !== account ||
        !z.uuid().safeParse(source.recommendationId).success ||
        !z.uuid().safeParse(source.sectionId).success
      )
        return 'stop'
      try {
        await markSeen({
          id: source.recommendationId,
          sectionId,
          guideRevisionId: revisionId,
        }).unwrap()
        return 'ok'
      } catch (error) {
        if (!isCurrent()) return 'stop'
        const failure = toStoryFailure(error)
        if (
          failure.status === 409 &&
          failure.code === 'GUIDE_REVISION_CHANGED'
        ) {
          try {
            const catalog = await loadCatalog(account, false).unwrap()
            return isCurrent() &&
              catalog.items.some(
                (item) =>
                  item.id === source.recommendationId &&
                  item.guideTarget.sectionId === sectionId,
              )
              ? 'refresh'
              : 'stop'
          } catch {
            return 'stop'
          }
        }
        return failure.status === 'network' ||
          failure.status === 429 ||
          (typeof failure.status === 'number' && failure.status >= 500)
          ? 'retry'
          : 'stop'
      }
    },
    [account, isCurrent, markSeen, loadCatalog],
  )
}
